<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="520px"
    :close-on-click-modal="false"
    :close-on-press-escape="!purchasing"
    :show-close="!purchasing"
    @close="handleClose"
    class="quick-purchase-dialog"
  >
    <div slot="title" class="dialog-title">
      {{ bizType === 'paper' ? '试卷购买' : bizType === 'class' ? '班级购买' : '课程购买' }}
    </div>

    <div class="purchase-content">
      <!-- 订单信息 -->
      <div class="order-info">
        <img v-if="image" :src="image" class="item-cover" />
        <div class="item-details">
          <div class="item-title">{{ title }}</div>
          <div class="item-price">
            <span class="price-label">价格：</span>
            <span class="price-value">¥{{ price.toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <!-- 支付方式 -->
      <div class="payment-section">
        <div class="payment-label">支付方式</div>
        <div class="payment-methods">
          <div
            class="method-btn"
            :class="{ active: payMethod === 'wechat' }"
            @click="switchPayMethod('wechat')"
          >
            <img src="@/assets/image/weixin.png" alt="微信" class="method-icon" />
            <span>微信支付</span>
          </div>
          <div
            class="method-btn"
            :class="{ active: payMethod === 'alipay' }"
            @click="switchPayMethod('alipay')"
          >
            <img src="@/assets/image/zhifubao.png" alt="支付宝" class="method-icon" />
            <span>支付宝支付</span>
          </div>
        </div>
      </div>

      <!-- 二维码区域 -->
      <div class="qr-section">
        <div class="qr-box" v-loading="requestingPayUrl || creatingOrder" element-loading-text="加载中...">
          <img v-if="qrCodeDataUrl && !requestingPayUrl" :src="qrCodeDataUrl" alt="支付二维码" class="qr-img" />
          <div v-else-if="!requestingPayUrl && !creatingOrder" class="qr-placeholder">
            {{ orderNo ? '暂无支付二维码，请点击重新获取' : '点击下方按钮创建订单' }}
          </div>
        </div>
        <div v-if="qrCodeDataUrl && !requestingPayUrl" class="qr-tip">
          请用{{ payMethod === 'wechat' ? '微信' : '支付宝' }}扫码，支付 <span class="price">¥{{ price.toFixed(2) }}</span>
        </div>
        <div v-else-if="requestingPayUrl" class="qr-tip">正在获取支付二维码...</div>
        <div v-else-if="creatingOrder" class="qr-tip">正在创建订单...</div>
        <div v-else class="qr-tip">{{ orderNo ? '点击下方按钮获取支付信息' : '创建订单后生成二维码' }}</div>

        <!-- 0元订单 -->
        <div v-if="zeroAmountOrder && orderNo" class="zero-amount-confirm">
          <p class="zero-tip">优惠券已抵扣全部金额，无需支付</p>
          <el-button type="primary" size="medium" :loading="confirmingZeroOrder" @click="confirmZeroAmountOrder">确认下单</el-button>
        </div>
      </div>

      <!-- 支付成功 -->
      <div v-if="purchaseSuccess" class="success-section">
        <i class="el-icon-circle-check"></i>
        <span>支付成功！</span>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose" :disabled="purchasing">取消</el-button>
      <el-button
        v-if="!purchaseSuccess"
        type="primary"
        :loading="purchasing || creatingOrder || requestingPayUrl"
        @click="handleConfirm"
      >
        {{ confirmButtonText }}
      </el-button>
      <el-button v-else type="primary" @click="handleGoLearn">去学习</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { createCourseOrder, createPaperOrder, createClassOrder, payOrder, getOrderPaymentStatus } from '@/api/orderApi'
import QRCode from 'qrcode'

export default {
  name: 'QuickPurchaseDialog',
  props: {
    visible: { type: Boolean, default: false },
    bizType: { type: String, default: 'course' },
    itemId: { type: [String, Number], required: true },
    title: { type: String, default: '' },
    image: { type: String, default: '' },
    price: { type: Number, default: 0 },
    distributorId: { type: [String, Number], default: null }
  },
  data() {
    return {
      dialogVisible: false,
      payMethod: 'wechat',
      orderNo: null,
      payUrl: '',
      qrCodeDataUrl: '',
      requestingPayUrl: false,
      creatingOrder: false,
      purchasing: false,
      zeroAmountOrder: false,
      confirmingZeroOrder: false,
      purchaseSuccess: false,
      pollingTimer: null
    }
  },
  computed: {
    confirmButtonText() {
      if (this.zeroAmountOrder && this.orderNo) return '确认下单'
      if (this.qrCodeDataUrl) return '已扫码，完成支付'
      if (this.orderNo) return '获取支付二维码'
      return '立即支付'
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) this.resetState()
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    resetState() {
      this.orderNo = null
      this.payUrl = ''
      this.qrCodeDataUrl = ''
      this.requestingPayUrl = false
      this.creatingOrder = false
      this.purchasing = false
      this.zeroAmountOrder = false
      this.confirmingZeroOrder = false
      this.purchaseSuccess = false
      this.payMethod = 'wechat'
      this.stopPolling()
    },
    handleClose() {
      if (this.purchasing) return
      this.dialogVisible = false
      this.stopPolling()
      if (this.purchaseSuccess) this.$emit('purchased')
    },
    switchPayMethod(method) {
      if (this.payMethod === method) return
      this.payMethod = method
      if (this.orderNo && this.price > 0) {
        this.payUrl = ''
        this.qrCodeDataUrl = ''
        this.requestPayUrl()
      }
    },
    async handleConfirm() {
      if (this.purchaseSuccess) {
        this.handleGoLearn()
        return
      }
      if (this.zeroAmountOrder && this.orderNo) {
        await this.confirmZeroAmountOrder()
        return
      }
      if (this.orderNo && this.qrCodeDataUrl) {
        await this.checkPaymentResult()
        return
      }
      if (this.orderNo && !this.qrCodeDataUrl) {
        await this.requestPayUrl()
        return
      }
      await this.createOrderAndPay()
    },
    async createOrderAndPay() {
      this.creatingOrder = true
      try {
        this.payUrl = ''
        this.qrCodeDataUrl = ''
        const payType = this.payMethod === 'wechat' ? 1 : 2
        const orderData = this.buildOrderData(payType)
        let res
        if (this.bizType === 'paper') {
          res = await createPaperOrder(orderData)
        } else if (this.bizType === 'class') {
          res = await createClassOrder(orderData)
        } else {
          res = await createCourseOrder(orderData)
        }
        const data = res && res.data != null ? res.data : null

        let orderNo = null
        if (data && typeof data === 'object') {
          orderNo = data.orderNo || data.order_no || data.outTradeNo || data.out_trade_no || data.orderId || data.id
        } else if (typeof data === 'string') {
          orderNo = data
        }
        if (!orderNo && orderNo !== 0) {
          this.$message.error('创建订单失败')
          return
        }
        this.orderNo = String(orderNo)

        // 0元订单
        const payAmount = data && typeof data === 'object' ? data.payAmount : null
        if (payAmount != null && Number(payAmount) === 0) {
          this.zeroAmountOrder = true
          return
        }
        if (this.price === 0) {
          this.zeroAmountOrder = true
          return
        }

        // 提取支付URL
        let payUrl = ''
        if (data && typeof data === 'object') {
          if (data.paymentParams) {
            payUrl = data.paymentParams.codeUrl || data.paymentParams.paymentUrl || data.paymentParams.qrUrl || ''
          }
          if (!payUrl) {
            payUrl = data.payUrl || data.qrUrl || data.url || ''
          }
        }
        if (payUrl) {
          this.payUrl = String(payUrl)
          await this.generateQRCode(this.payUrl)
          this.startPolling()
          return
        }
        await this.requestPayUrl()
      } catch (err) {
        this.$message.error(err && err.message || '创建订单失败')
      } finally {
        this.creatingOrder = false
      }
    },
    buildOrderData(payType) {
      const base = { payType, cashAmount: this.price, coinAmount: 0, couponAmount: 0 }
      if (this.bizType === 'course') {
        base.courseId = this.itemId
        if (this.distributorId) base.distributorId = this.distributorId
      } else if (this.bizType === 'paper') {
        base.paperId = this.itemId
      } else if (this.bizType === 'class') {
        base.classId = this.itemId
      }
      return base
    },
    async requestPayUrl() {
      if (this.requestingPayUrl || !this.orderNo) return
      this.requestingPayUrl = true
      try {
        this.payUrl = ''
        this.qrCodeDataUrl = ''
        const paymentType = this.payMethod === 'alipay' ? 2 : 1
        const res = await payOrder({ orderNo: this.orderNo, paymentType })
        const data = res && res.data != null ? res.data : null

        let nextPayUrl = ''
        if (typeof data === 'string') {
          nextPayUrl = data
        } else if (data && typeof data === 'object') {
          if (data.paymentParams) {
            nextPayUrl = data.paymentParams.codeUrl || data.paymentParams.paymentUrl || data.paymentParams.qrUrl || ''
          }
          if (!nextPayUrl) {
            nextPayUrl = data.payUrl || data.qrUrl || data.url || ''
          }
        }
        if (!nextPayUrl) {
          this.$message.error('发起支付失败')
          return
        }
        this.payUrl = String(nextPayUrl).trim()
        await this.generateQRCode(this.payUrl)
        this.startPolling()
      } catch (err) {
        this.$message.error(err && err.message || '发起支付失败')
      } finally {
        this.requestingPayUrl = false
      }
    },
    async generateQRCode(url) {
      if (!url) { this.qrCodeDataUrl = ''; return }
      try {
        this.qrCodeDataUrl = await QRCode.toDataURL(url, {
          width: 200, margin: 1,
          color: { dark: '#000000', light: '#ffffff' }
        })
      } catch (e) {
        this.qrCodeDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`
      }
    },
    async confirmZeroAmountOrder() {
      this.confirmingZeroOrder = true
      try {
        const paymentType = this.payMethod === 'alipay' ? 2 : 1
        const res = await payOrder({ orderNo: this.orderNo, paymentType })
        if (res && res.code === 200) {
          this.purchaseSuccess = true
          this.zeroAmountOrder = false
          this.$message.success('购买成功')
        } else {
          this.$message.error(res && res.message || '确认失败')
        }
      } catch (err) {
        this.$message.error(err && err.message || '确认失败')
      } finally {
        this.confirmingZeroOrder = false
      }
    },
    async checkPaymentResult() {
      this.purchasing = true
      try {
        const res = await getOrderPaymentStatus(this.orderNo)
        const data = res && res.data
        if (data && (data.status === 1 || data.status === '1' || data.isPaid)) {
          this.purchaseSuccess = true
          this.stopPolling()
          this.$message.success('支付成功')
        } else {
          this.$message.info('未检测到支付成功，请稍后重试')
        }
      } catch (err) {
        this.$message.error(err && err.message || '查询支付状态失败')
      } finally {
        this.purchasing = false
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollingTimer = setInterval(async () => {
        if (!this.orderNo) return
        try {
          const res = await getOrderPaymentStatus(this.orderNo)
          const data = res && res.data
          if (data && (data.status === 1 || data.status === '1' || data.isPaid)) {
            this.purchaseSuccess = true
            this.stopPolling()
            this.$message.success('支付成功')
          }
        } catch (e) { /* ignore */ }
      }, 3000)
    },
    stopPolling() {
      if (this.pollingTimer) { clearInterval(this.pollingTimer); this.pollingTimer = null }
    },
    handleGoLearn() {
      this.dialogVisible = false
      this.$emit('purchased')
      const routeName = this.bizType === 'class' ? 'classStudyDetail' : 'learn'
      this.$router.push({ name: routeName, params: { id: this.itemId } })
    }
  }
}
</script>

<style scoped lang="scss">
.quick-purchase-dialog {
  ::v-deep .el-dialog__header {
    border-bottom: 1px solid #eee;
    padding: 16px 20px;
  }
  ::v-deep .el-dialog__body { padding: 20px; }
  ::v-deep .el-dialog__footer { border-top: 1px solid #eee; padding: 16px 20px; }
}

.dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.purchase-content {
  .order-info {
    display: flex;
    align-items: center;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
    margin-bottom: 20px;

    .item-cover {
      width: 100px;
      height: 75px;
      object-fit: cover;
      border-radius: 4px;
      margin-right: 15px;
    }
    .item-details {
      flex: 1;
      .item-title { font-size: 15px; font-weight: 500; color: #333; margin-bottom: 8px; }
      .item-price {
        .price-label { font-size: 14px; color: #666; }
        .price-value { font-size: 20px; font-weight: 700; color: #ff4d4f; }
      }
    }
  }

  .payment-section {
    margin-bottom: 20px;
    .payment-label { font-size: 14px; color: #333; margin-bottom: 12px; font-weight: 500; }
    .payment-methods {
      display: flex;
      gap: 12px;
      .method-btn {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px;
        border: 2px solid #eee;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 14px;
        color: #333;
        &:hover { border-color: #ccc; }
        &.active { border-color: #3E2CCD; background: #f5f3ff; }
        .method-icon { width: 24px; height: 24px; }
      }
    }
  }

  .qr-section {
    text-align: center;
    padding: 10px 0;
    .qr-box {
      display: inline-block;
      width: 200px;
      height: 200px;
      border: 1px solid #eee;
      border-radius: 4px;
      overflow: hidden;
      position: relative;
      min-height: 200px;
      .qr-img { width: 100%; height: 100%; }
      .qr-placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #999;
        font-size: 13px;
        padding: 10px;
        box-sizing: border-box;
      }
    }
    .qr-tip {
      margin-top: 12px;
      font-size: 14px;
      color: #666;
      .price { color: #ff4d4f; font-weight: 600; }
    }
    .zero-amount-confirm {
      margin-top: 15px;
      .zero-tip { font-size: 14px; color: #666; margin-bottom: 10px; }
    }
  }

  .success-section {
    text-align: center;
    padding: 20px 0;
    color: #52c41a;
    font-size: 18px;
    font-weight: 600;
    i { font-size: 28px; margin-right: 8px; vertical-align: middle; }
  }
}

.dialog-footer { text-align: center; }
</style>